import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

/**
 * Generate a secure invitation token
 */
const generateInvitationToken = () => {
  return uuidv4()?.replace(/-/g, '');
};

/**
 * Create a new user invitation
 */
export const createInvitation = async ({
  email,
  role,
  parentAdminId = null,
  centerIds = [],
  expiresInDays = 7
}) => {
  try {
    // Get current user
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate invitation token
    const token = generateInvitationToken();
    const expiresAt = new Date();
    expiresAt?.setDate(expiresAt?.getDate() + expiresInDays);

    // Create invitation record
    const { data: invitation, error: invitationError } = await supabase?.from('user_invitations')?.insert({
        email,
        invited_role: role,
        invited_by: user?.id,
        parent_admin_id: parentAdminId,
        invitation_token: token,
        expires_at: expiresAt?.toISOString(),
        status: 'pending'
      })?.select()?.single();

    if (invitationError) throw invitationError;

    // Create center allocations if provided
    if (centerIds?.length > 0) {
      const allocations = centerIds?.map(centerId => ({
        invitation_id: invitation?.id,
        center_id: centerId
      }));

      const { error: allocationError } = await supabase?.from('invitation_center_allocations')?.insert(allocations);

      if (allocationError) throw allocationError;
    }

    return { success: true, invitation, token };
  } catch (error) {
    console.error('Error creating invitation:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send invitation email via Resend edge function
 */
export const sendInvitationEmail = async ({
  email,
  inviterName,
  role,
  centers = [],
  token,
  expiresAt
}) => {
  try {
    // Get Supabase project URL for edge function
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/send-invitation-email`;

    // Create invitation link
    const appUrl = window.location?.origin;
    const invitationLink = `${appUrl}/accept-invitation?token=${token}`;

    // Call edge function
    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        inviterName,
        role,
        centers,
        invitationLink,
        expiresAt
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    return { success: true, messageId: result?.messageId };
  } catch (error) {
    console.error('Error sending invitation email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Create invitation and send email in one operation
 */
export const inviteUser = async ({
  email,
  role,
  parentAdminId = null,
  centerIds = [],
  expiresInDays = 7
}) => {
  try {
    // Create invitation
    const createResult = await createInvitation({
      email,
      role,
      parentAdminId,
      centerIds,
      expiresInDays
    });

    if (!createResult?.success) {
      return createResult;
    }

    // Get inviter details
    const { data: inviterProfile } = await supabase?.from('user_profiles')?.select('full_name')?.single();

    const inviterName = inviterProfile?.full_name || 'Admin';

    // Get center names if provided
    let centerNames = [];
    if (centerIds?.length > 0) {
      const { data: centers } = await supabase?.from('centers')?.select('name')?.in('id', centerIds);
      
      centerNames = centers?.map(c => c?.name) || [];
    }

    // Send email
    const emailResult = await sendInvitationEmail({
      email,
      inviterName,
      role,
      centers: centerNames,
      token: createResult?.token,
      expiresAt: createResult?.invitation?.expires_at
    });

    if (!emailResult?.success) {
      // Email failed but invitation created
      return {
        success: true,
        warning: 'Invitation created but email failed to send',
        invitation: createResult?.invitation,
        emailError: emailResult?.error
      };
    }

    return {
      success: true,
      invitation: createResult?.invitation,
      messageId: emailResult?.messageId
    };
  } catch (error) {
    console.error('Error inviting user:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Get all invitations (with filters)
 */
export const getInvitations = async (filters = {}) => {
  try {
    let query = supabase?.from('user_invitations')?.select(`
        *,
        invited_by_profile:user_profiles!user_invitations_invited_by_fkey(full_name, email),
        parent_admin_profile:user_profiles!user_invitations_parent_admin_id_fkey(full_name, email),
        invitation_center_allocations(
          center_id,
          centers(name)
        )
      `)?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.role) {
      query = query?.eq('invited_role', filters?.role);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching invitations:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Cancel an invitation
 */
export const cancelInvitation = async (invitationId) => {
  try {
    const { error } = await supabase?.from('user_invitations')?.update({ status: 'cancelled' })?.eq('id', invitationId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Resend invitation email
 */
export const resendInvitation = async (invitationId) => {
  try {
    // Get invitation details
    const { data: invitation, error: fetchError } = await supabase?.from('user_invitations')?.select(`
        *,
        invited_by_profile:user_profiles!user_invitations_invited_by_fkey(full_name),
        invitation_center_allocations(
          centers(name)
        )
      `)?.eq('id', invitationId)?.single();

    if (fetchError) throw fetchError;

    // Check if invitation is still valid
    if (invitation?.status !== 'pending') {
      throw new Error('Can only resend pending invitations');
    }

    if (new Date(invitation.expires_at) < new Date()) {
      throw new Error('Invitation has expired');
    }

    // Extract center names
    let centerNames = invitation?.invitation_center_allocations?.map(
      alloc => alloc?.centers?.name
    )?.filter(Boolean) || [];

    // Send email
    const emailResult = await sendInvitationEmail({
      email: invitation?.email,
      inviterName: invitation?.invited_by_profile?.full_name || 'Admin',
      role: invitation?.invited_role,
      centers: centerNames,
      token: invitation?.invitation_token,
      expiresAt: invitation?.expires_at
    });

    if (!emailResult?.success) {
      throw new Error(emailResult.error);
    }

    return { success: true, messageId: emailResult?.messageId };
  } catch (error) {
    console.error('Error resending invitation:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Verify invitation token
 */
export const verifyInvitationToken = async (token) => {
  try {
    const { data: invitation, error } = await supabase?.from('user_invitations')?.select(`
        *,
        invitation_center_allocations(
          center_id,
          centers(name)
        )
      `)?.eq('invitation_token', token)?.eq('status', 'pending')?.single();

    if (error) throw error;

    // Check expiration
    if (new Date(invitation.expires_at) < new Date()) {
      // Mark as expired
      await supabase?.from('user_invitations')?.update({ status: 'expired' })?.eq('id', invitation?.id);

      throw new Error('Invitation has expired');
    }

    return { success: true, invitation };
  } catch (error) {
    console.error('Error verifying invitation:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Accept invitation (called after user signs up)
 */
export const acceptInvitation = async (token, userId) => {
  try {
    // Verify invitation
    const verifyResult = await verifyInvitationToken(token);
    if (!verifyResult?.success) {
      return verifyResult;
    }

    const invitation = verifyResult?.invitation;

    // Update user profile with role and parent admin
    const { error: updateError } = await supabase?.from('user_profiles')?.update({
        role: invitation?.invited_role,
        parent_admin_id: invitation?.parent_admin_id
      })?.eq('id', userId);

    if (updateError) throw updateError;

    // Create center allocations if any
    const centerIds = invitation?.invitation_center_allocations?.map(
      alloc => alloc?.center_id
    ) || [];

    if (centerIds?.length > 0) {
      const allocations = centerIds?.map(centerId => ({
        admin_id: userId,
        center_id: centerId,
        allocated_by: invitation?.invited_by
      }));

      const { error: allocationError } = await supabase?.from('admin_center_allocations')?.insert(allocations);

      if (allocationError) throw allocationError;
    }

    // Mark invitation as accepted
    const { error: acceptError } = await supabase?.from('user_invitations')?.update({
        status: 'accepted',
        accepted_at: new Date()?.toISOString()
      })?.eq('id', invitation?.id);

    if (acceptError) throw acceptError;

    return { success: true };
  } catch (error) {
    console.error('Error accepting invitation:', error);
    return { success: false, error: error?.message };
  }
};